import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  FlatList,
  Alert,
  TextInput,
  StyleSheet,
  ActivityIndicator,
} from "react-native";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "@/lib/firebase";
import {
  adminListCitySuggestionsGlobal,
  adminListAreaSuggestionsGlobal,
  adminApproveCitySuggestion,
  adminRejectCitySuggestion,
  adminApproveAreaSuggestion,
  adminRejectAreaSuggestion,
  type AdminCitySuggestion,
  type AdminAreaSuggestion,
} from "@/lib/locationsAdmin";

type Item =
  | ({ kind: "citySuggestion" } & AdminCitySuggestion)
  | ({ kind: "areaSuggestion" } & AdminAreaSuggestion);

export default function AdminLocationsQueue() {
  const adminUid = auth.currentUser?.uid;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [search, setSearch] = useState("");

  async function debugAdminRole(): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid) return "Not signed in.";

    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (!snap.exists()) return `users/${uid} is missing (role doc not found).`;
      const role = (snap.data() as any)?.role;
      return `users/${uid}.role = ${String(role ?? "(none)")}`;
    } catch (e: any) {
      return `Failed to read users/${uid}: ${e?.message ?? String(e)}`;
    }
  }

  async function load() {
    if (!adminUid) return;
    setLoading(true);

    try {
      let citySugs: AdminCitySuggestion[] = [];
      let areaSugs: AdminAreaSuggestion[] = [];

      try {
        citySugs = await adminListCitySuggestionsGlobal(db, { status: "pending" });
      } catch (e: any) {
        const adminInfo = await debugAdminRole();
        Alert.alert(
          "Error loading pending city suggestions",
          `${e?.message ?? String(e)}\n\nAdmin check: ${adminInfo}`
        );
        throw e;
      }

      try {
        areaSugs = await adminListAreaSuggestionsGlobal(db, { status: "pending" });
      } catch (e: any) {
        const adminInfo = await debugAdminRole();
        Alert.alert(
          "Error loading pending area suggestions",
          `${e?.message ?? String(e)}\n\nAdmin check: ${adminInfo}`
        );
        throw e;
      }

      const merged: Item[] = [
        ...citySugs.map((c) => ({ ...c, kind: "citySuggestion" as const })),
        ...areaSugs.map((a) => ({ ...a, kind: "areaSuggestion" as const })),
      ];

      merged.sort((a, b) => {
        const ta = (a as any)?.createdAt?.seconds ?? 0;
        const tb = (b as any)?.createdAt?.seconds ?? 0;
        return tb - ta;
      });

      setItems(merged);
    } catch {
      // alerts already shown
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;

    return items.filter((x) => {
      const text =
        `${(x as any)?.label ?? ""} ${(x as any)?.countyName ?? ""} ${(x as any)?.cityName ?? ""} ${(x as any)?.citySuggestionName ?? ""}`.toLowerCase();
      return text.includes(q);
    });
  }, [items, search]);

  async function approve(item: Item) {
    if (!adminUid) return;

    try {
      if (item.kind === "citySuggestion") {
        await adminApproveCitySuggestion(db, {
          suggestionPath: item._path!,
          countyId: item.countyId,
          countyName: item.countyName ?? "",
          label: item.label,
        });
      } else {
        await adminApproveAreaSuggestion(db, {
          suggestionPath: item._path!,
          countyId: item.countyId,
          countyName: item.countyName ?? "",
          label: item.label,
          // Either approved cityId exists, OR citySuggestionId exists (pending city)
          cityId: item.cityId ?? null,
          cityName: item.cityName ?? null,
          citySuggestionId: item.citySuggestionId ?? null,
          citySuggestionName: item.citySuggestionName ?? null,
        });
      }

      await load();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Approve failed.");
    }
  }

  async function reject(item: Item) {
    if (!adminUid) return;

    const name = (item as any)?.label ?? "this item";

    Alert.alert("Reject item?", `Reject "${name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            if (item.kind === "citySuggestion") {
              await adminRejectCitySuggestion(db, { suggestionPath: item._path! });
            } else {
              await adminRejectAreaSuggestion(db, { suggestionPath: item._path! });
            }
            await load();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Reject failed.");
          }
        },
      },
    ]);
  }

  return (
    <View style={styles.container}>
      <Text style={styles.h1}>Pending Location Suggestions</Text>

      <TextInput
        value={search}
        onChangeText={setSearch}
        placeholder="Search pending…"
        style={styles.input}
        autoCorrect={false}
      />

      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading…</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => `${item.kind}:${item._path ?? item.id}`}
          ItemSeparatorComponent={() => <View style={styles.sep} />}
          renderItem={({ item }) => {
            const title = `${item.label} `;

            return (
              <View style={styles.card}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.title}>
                    {title}
                    <Text style={styles.badge}>
                      {item.kind === "citySuggestion" ? "City" : "Area"}
                    </Text>
                  </Text>

                  <Text style={styles.meta}>
                    County: {item.countyName ?? item.countyId ?? "-"}
                  </Text>

                  {item.kind === "areaSuggestion" ? (
                    <>
                      {item.cityId ? (
                        <Text style={styles.meta}>Approved CityId: {item.cityId}</Text>
                      ) : (
                        <Text style={styles.meta}>
                          Pending City:{" "}
                          {item.citySuggestionName ?? item.citySuggestionId ?? "-"}
                        </Text>
                      )}
                    </>
                  ) : null}

                  <Text style={styles.meta}>
                    CreatedByUid: {(item as any).createdByUid ?? "-"}
                  </Text>
                </View>

                <View style={styles.actions}>
                  <Pressable onPress={() => approve(item)} style={[styles.btn, styles.btnGood]}>
                    <Text style={[styles.btnText, styles.btnTextGood]}>Approve</Text>
                  </Pressable>

                  <Pressable onPress={() => reject(item)} style={[styles.btn, styles.btnBad]}>
                    <Text style={[styles.btnText, styles.btnTextBad]}>Reject</Text>
                  </Pressable>
                </View>
              </View>
            );
          }}
          ListEmptyComponent={
            <View style={{ padding: 16 }}>
              <Text style={{ opacity: 0.7 }}>No pending location suggestions.</Text>
            </View>
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: "#fff" },
  h1: { fontSize: 18, fontWeight: "800", marginBottom: 10 },
  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  loadingRow: { flexDirection: "row", gap: 8, alignItems: "center", padding: 12 },
  sep: { height: 10 },
  card: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },
  title: { fontSize: 15, fontWeight: "800" },
  badge: { fontSize: 12, fontWeight: "800", opacity: 0.6 },
  meta: { marginTop: 4, opacity: 0.7, fontSize: 12 },
  actions: { gap: 8, alignItems: "flex-end" },
  btn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  btnText: { fontWeight: "800" },
  btnGood: { borderColor: "#2e7d32" },
  btnTextGood: { color: "#2e7d32" },
  btnBad: { borderColor: "#c62828" },
  btnTextBad: { color: "#c62828" },
});